import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  addOnboardingSource,
  analyzeOnboarding,
  cancelOnboardingSession,
  connectModelFanslyAccount,
  connectModelOnlyfansApiAccount,
  connectModelOnlyMonsterAccount,
  createOnboardingRelease,
  evaluateOnboardingReadiness,
  getApiErrorMessage,
  getOnboardingSession,
  importOnboardingHistory,
  listFanslyAuthAccounts,
  listOnboardingSessions,
  publishOnboardingRelease,
  removeOnboardingSource,
  reviewOnboardingCandidate,
  reviewOnboardingFinding,
  rollbackOnboardingRelease,
  startOnboardingSession,
  startFanvuePlatformOAuth,
  testOnboardingRelease,
  updateOnboardingDraft,
  updateOnboardingMonetization,
  updateOnboardingSession,
  uploadOnboardingSource,
} from '../../api'
import {
  EmptyState,
  LoadingBlock,
  MetricCard,
  Section,
  StatusPill,
} from '../../components/v2/V2Primitives'
import { asList, modelTitle, statusTone } from './v2Utils'
import { rememberFanvueOAuthSession } from './fanvueOAuthState'
import { useWorkspaceScope } from '../../contexts/WorkspaceScopeContext'
import { canPublishOnboardingRelease, resolveOnboardingModelId } from '../../lib/onboardingWorkspace'
import CreateModelWorkspace from '../../components/CreateModelWorkspace'

const STEPS = [
  ['connection', 'Connect'],
  ['import', 'Add information'],
  ['review', 'Review AI draft'],
  ['audit', 'Audit old chats'],
  ['configure', 'Voice & behavior'],
  ['monetization', 'Products & content'],
  ['test', 'Test & publish'],
]

const STEP_HELP = {
  connection: { required: true, need: 'Choose the creator platforms and connect at least one account.', result: 'FanLTV can securely read and send messages for this model.' },
  import: { required: true, need: 'Add at least one creator brief or approved document. Historical chat import is optional.', result: 'AI receives evidence for the model identity, voice, boundaries and selling style.' },
  review: { required: true, need: 'Approve, edit or reject the facts suggested by AI.', result: 'Only reviewed information enters the model draft.' },
  audit: { required: false, need: 'Review strengths, revenue leaks and inconsistent chat behavior found in old chats.', result: 'You decide what the new bot should preserve, fix, stop or test.' },
  configure: { required: true, need: 'Answer simple questions about how the model should chat and move toward a sale.', result: 'Your choices are compiled into real graph settings; no prompt editing is required.' },
  monetization: { required: true, need: 'Enable only products the creator can really sell, then fill in settings for those products.', result: 'The bot knows what it may offer, how it is fulfilled and when a person must take over.' },
  test: { required: true, need: 'Run readiness and isolated conversations before publishing.', result: 'The exact reviewed version is tested without unpausing live automation.' },
}

const EMPTY_BEHAVIOR_PLAN = {
  schema_version: 'model-onboarding-behavior-v1',
  confirmed: false,
  conversation_lead: 'balanced',
  sales_pacing: 'balanced',
  presell_privacy_check: 'contextual',
  offer_after_positive_reaction: true,
  free_preview_then_paid: true,
  reply_style: 'natural',
  punctuation_style: 'minimal',
  delivery_style: 'natural_bursts',
  emoji_policy: 'mirror_fan',
  explicit_buy_flow: 'direct',
  price_objection_strategy: 'ask_budget',
  proactive_followup: 'adaptive',
}

const CONNECTION_PLATFORMS = [
  { key: 'onlyfans', name: 'OnlyFans', detail: 'Connect through OnlyFansAPI or OnlyMonster.' },
  { key: 'fansly', name: 'Fansly', detail: 'Validate the OnlyFansAPI key and select the creator account.' },
  { key: 'fanvue', name: 'Fanvue', detail: 'Connect securely through the shared Fanvue OAuth app.' },
]

const DRAFT_SECTIONS = [
  'persona',
  'bio',
  'appearance',
  'voice',
  'boundaries',
  'capabilities',
  'sales',
  'content',
  'public_identity',
  'private_identity',
  'dynamic_facts',
  'traffic',
]

const AUDIT_BUCKETS = ['preserve', 'fix', 'stop', 'experiment']

const ROUTE_META = {
  catalog_ppv: { title: 'Catalog PPV', description: 'Sell real, available catalog media when the conversation is ready.' },
  subscriptions: { title: 'Subscriptions', description: 'Consult on configured tiers and guide the fan toward a truthful fit.' },
  customs: { title: 'Custom content', description: 'Collect requirements within confirmed production boundaries and pricing.' },
  tips_support: { title: 'Tips & support', description: 'Use only configured voluntary support moments and cooldowns.' },
  wishlist: { title: 'Wishlist', description: 'Share one verified public wishlist without inventing needs.' },
  physical_items: { title: 'Physical items', description: 'Collect interest and hand fulfilment to a person.' },
}

const ROUTE_MODES = {
  catalog_ppv: [['disabled', 'Disabled'], ['autonomous', 'Bot managed']],
  subscriptions: [['disabled', 'Disabled'], ['autonomous', 'Bot managed']],
  customs: [['disabled', 'Disabled'], ['human_handoff', 'Detect & handoff'], ['autonomous', 'Bot negotiates']],
  tips_support: [['disabled', 'Disabled'], ['autonomous', 'Bot managed']],
  wishlist: [['disabled', 'Disabled'], ['autonomous', 'Bot managed']],
  physical_items: [['disabled', 'Disabled'], ['human_handoff', 'Detect & handoff']],
}

const EMPTY_MONETIZATION_PLAN = {
  schema_version: 'model-onboarding-monetization-v1',
  confirmed: false,
  routes: Object.fromEntries(Object.keys(ROUTE_META).map((key) => [key, {
    mode: 'disabled',
    ...(key === 'catalog_ppv' ? { strategy: 'intent_based' } : { config: {} }),
  }])),
}

function formatDate(value) {
  if (!value) return '—'
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(value))
  } catch {
    return String(value)
  }
}

function displayValue(value) {
  if (typeof value === 'string') return value
  return JSON.stringify(value, null, 2)
}

function displayRows(value, prefix = '') {
  if (Array.isArray(value)) {
    if (!value.length) return [{ label: prefix || 'Value', value: 'None' }]
    return value.flatMap((item, index) => displayRows(item, prefix ? `${prefix} · ${index + 1}` : `Item ${index + 1}`))
  }
  if (value && typeof value === 'object') {
    const rows = Object.entries(value).flatMap(([key, item]) => (
      displayRows(item, prefix ? `${prefix} · ${key.replaceAll('_', ' ')}` : key.replaceAll('_', ' '))
    ))
    return rows.length ? rows : [{ label: prefix || 'Value', value: 'None' }]
  }
  return [{
    label: prefix || 'Value',
    value: value == null ? 'None' : typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value),
  }]
}

function parseEditorValue(value) {
  const text = String(value || '').trim()
  if (!text) return ''
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

function evidenceText(evidence = []) {
  return evidence
    .slice(0, 3)
    .map((item) => `${item.message_index ? `#${item.message_index} ` : ''}${item.quote || ''}`)
    .filter(Boolean)
    .join(' · ')
}

function countByStatus(items = [], status) {
  return items.filter((item) => item.status === status).length
}

function DraftToggle({ label, checked, onChange }) {
  return (
    <label className="v2-check-row">
      <input type="checkbox" checked={Boolean(checked)} onChange={(event) => onChange(event.target.checked)} />
      <span>{label}</span>
    </label>
  )
}

function RouteConfigFields({
  routeKey,
  route,
  modelId,
  contentInventory,
  onRouteChange,
  onConfigChange,
}) {
  if (route.mode === 'disabled') return <p className="v2-onboarding-muted">No setup required while this route is disabled.</p>
  const config = route.config || {}
  if (routeKey === 'catalog_ppv') {
    return (
      <div className="v2-onboarding-route-config">
        <label className="v2-field"><span>Sales strategy</span><select value={route.strategy || 'intent_based'} onChange={(event) => onRouteChange({ strategy: event.target.value })}><option value="intent_based">Intent based — scripts optional</option><option value="scripted">Scripted funnel — valid script required</option><option value="hybrid">Hybrid — catalog + scripts</option></select></label>
        <div className="v2-onboarding-route-facts"><span>{contentInventory.eligible || 0} eligible media</span><span>{contentInventory.described || 0} described</span><span>{contentInventory.catalog_sellable || 0} catalog sale items</span><span>{contentInventory.catalog_sellable_described || 0} sale items described</span><span>{contentInventory.valid_scripts || 0} valid scripts</span></div>
        <Link className="v2-btn v2-btn-secondary" to={`/v2/models/${modelId}?tab=Content`}>Open media & sale scripts</Link>
      </div>
    )
  }
  if (routeKey === 'subscriptions') {
    const products = Array.isArray(config.products) ? config.products : []
    const providers = Array.isArray(config.providers) ? config.providers : []
    const setSubscriptionConfig = (patch) => onRouteChange({ config: { ...config, ...patch } })
    const updateProduct = (index, patch) => {
      const previousId = products[index]?.id
      const next = {
        products: products.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)),
      }
      if (patch.id !== undefined && config.default_product_id === previousId) next.default_product_id = patch.id
      setSubscriptionConfig(next)
    }
    const removeProduct = (index) => {
      const removedId = products[index]?.id
      setSubscriptionConfig({
        products: products.filter((_, itemIndex) => itemIndex !== index),
        default_product_id: config.default_product_id === removedId ? '' : config.default_product_id,
      })
    }
    const toggleProvider = (provider, enabled) => setSubscriptionConfig({
      providers: enabled
        ? Array.from(new Set([...providers, provider]))
        : providers.filter((item) => item !== provider),
    })
    return (
      <div className="v2-onboarding-route-config">
        <p><strong>Where can a fan buy this subscription?</strong></p>
        <div className="v2-onboarding-toggle-grid">
          {[['fansly', 'Fansly'], ['onlyfansapi', 'OnlyFans via OnlyFansAPI'], ['fanltv_ofapi', 'OnlyFans direct'], ['fanvue', 'Fanvue']].map(([provider, label]) => <DraftToggle key={provider} label={label} checked={providers.includes(provider)} onChange={(value) => toggleProvider(provider, value)} />)}
        </div>
        <label className="v2-field"><span>Default tier</span><select value={config.default_product_id || ''} onChange={(event) => setSubscriptionConfig({ default_product_id: event.target.value })}><option value="">Choose automatically</option>{products.filter((item) => item.enabled !== false).map((item) => <option key={item.id} value={item.id}>{item.name || item.id}</option>)}</select></label>
        <div className="v2-onboarding-subscription-list">
          {products.map((product, index) => (
            <article key={`${product.id || 'tier'}-${index}`} className="v2-onboarding-subscription-product">
              <div className="v2-onboarding-review-head"><strong>{product.name || `Tier ${index + 1}`}</strong><button type="button" className="v2-btn v2-btn-ghost" onClick={() => removeProduct(index)}>Remove</button></div>
              <DraftToggle label="Available for sale" checked={product.enabled !== false} onChange={(value) => updateProduct(index, { enabled: value })} />
              <div className="v2-form-grid">
                <label className="v2-field"><span>Name</span><input value={product.name || ''} onChange={(event) => updateProduct(index, { name: event.target.value })} /></label>
                <label className="v2-field"><span>Price</span><input type="number" min="0" step="0.01" value={product.price ?? ''} onChange={(event) => updateProduct(index, { price: event.target.value === '' ? '' : Number(event.target.value) })} /></label>
                <label className="v2-field"><span>Currency</span><select value={product.currency || 'USD'} onChange={(event) => updateProduct(index, { currency: event.target.value })}><option>USD</option><option>EUR</option><option>GBP</option></select></label>
              </div>
              <label className="v2-field"><span>Truthful value summary</span><textarea rows="2" value={product.consult_summary || ''} onChange={(event) => updateProduct(index, { consult_summary: event.target.value })} /></label>
              <label className="v2-field"><span>Benefits (one per line)</span><textarea rows="3" value={(product.benefits || []).join('\n')} onChange={(event) => updateProduct(index, { benefits: event.target.value.split('\n').map((item) => item.trim()).filter(Boolean) })} /></label>
              <details className="v2-advanced-disclosure"><summary>Advanced matching and provider IDs</summary><div className="v2-form-grid"><label className="v2-field"><span>Stable internal ID</span><input value={product.id || ''} onChange={(event) => updateProduct(index, { id: event.target.value })} placeholder="vip_monthly" /></label><label className="v2-field"><span>Provider tier ID (optional)</span><input value={product.provider_tier_id || ''} onChange={(event) => updateProduct(index, { provider_tier_id: event.target.value })} /></label><label className="v2-field"><span>Other names fans use</span><input value={(product.aliases || []).join(', ')} onChange={(event) => updateProduct(index, { aliases: event.target.value.split(',').map((item) => item.trim()).filter(Boolean) })} placeholder="vip, premium, private court" /></label><label className="v2-field"><span>Good-fit interests</span><input value={(product.fit_keywords || []).join(', ')} onChange={(event) => updateProduct(index, { fit_keywords: event.target.value.split(',').map((item) => item.trim()).filter(Boolean) })} /></label></div></details>
            </article>
          ))}
        </div>
        <button type="button" className="v2-btn v2-btn-secondary" onClick={() => setSubscriptionConfig({ products: [...products, { id: `tier_${products.length + 1}`, name: '', price: '', currency: 'USD', enabled: true, benefits: [] }] })}>Add subscription tier</button>
        <p>Each enabled tier needs a stable ID, name, real price and truthful value description. Nothing changes live until Publish.</p>
      </div>
    )
  }
  if (routeKey === 'customs') {
    return (
      <div className="v2-onboarding-route-config">
        <div className="v2-onboarding-toggle-grid">
          <DraftToggle label="Capabilities confirmed by the model" checked={config.custom_capabilities_configured} onChange={(value) => onConfigChange('custom_capabilities_configured', value)} />
          <DraftToggle label="Photos" checked={config.custom_photo_enabled} onChange={(value) => onConfigChange('custom_photo_enabled', value)} />
          <DraftToggle label="Video" checked={config.custom_video_enabled} onChange={(value) => onConfigChange('custom_video_enabled', value)} />
          <DraftToggle label="Audio / voice" checked={config.custom_audio_enabled} onChange={(value) => onConfigChange('custom_audio_enabled', value)} />
          <DraftToggle label="Name personalization" checked={config.custom_name_personalization_enabled} onChange={(value) => onConfigChange('custom_name_personalization_enabled', value)} />
          <DraftToggle label="Face may be shown" checked={config.custom_face_allowed} onChange={(value) => onConfigChange('custom_face_allowed', value)} />
        </div>
        <div className="v2-form-grid">
          <label className="v2-field"><span>Maximum nudity</span><select value={config.custom_max_nudity_level || 'clothed'} onChange={(event) => onConfigChange('custom_max_nudity_level', event.target.value)}><option value="clothed">Clothed</option><option value="lingerie">Lingerie</option><option value="topless">Topless</option><option value="full_nude">Full nude</option><option value="explicit_closeup">Explicit close-up</option></select></label>
        </div>
        {route.mode === 'autonomous' ? <><DraftToggle label="Allow the bot to calculate approved prices" checked={config.custom_pricing_enabled} onChange={(value) => onConfigChange('custom_pricing_enabled', value)} /><div className="v2-form-grid">
          <label className="v2-field"><span>Currency</span><select value={config.custom_pricing_currency || 'USD'} onChange={(event) => onConfigChange('custom_pricing_currency', event.target.value)}><option>USD</option><option>EUR</option><option>GBP</option></select></label>
          <label className="v2-field"><span>Minimum order</span><input type="number" min="0" value={config.custom_pricing_minimum_total ?? 75} onChange={(event) => onConfigChange('custom_pricing_minimum_total', Number(event.target.value))} /></label>
          <label className="v2-field"><span>Base production fee</span><input type="number" min="0" value={config.custom_pricing_base_fee ?? 30} onChange={(event) => onConfigChange('custom_pricing_base_fee', Number(event.target.value))} /></label>
          <label className="v2-field"><span>Video / minute</span><input type="number" min="0" value={config.custom_pricing_video_per_minute ?? 20} onChange={(event) => onConfigChange('custom_pricing_video_per_minute', Number(event.target.value))} /></label>
          <label className="v2-field"><span>Audio / minute</span><input type="number" min="0" value={config.custom_pricing_audio_per_minute ?? 8} onChange={(event) => onConfigChange('custom_pricing_audio_per_minute', Number(event.target.value))} /></label>
          <label className="v2-field"><span>Price / photo</span><input type="number" min="0" value={config.custom_pricing_photo_each ?? 10} onChange={(event) => onConfigChange('custom_pricing_photo_each', Number(event.target.value))} /></label>
          <label className="v2-field"><span>Name personalization markup, %</span><input type="number" min="0" value={config.custom_pricing_name_markup_percent ?? 20} onChange={(event) => onConfigChange('custom_pricing_name_markup_percent', Number(event.target.value))} /></label>
          <label className="v2-field"><span>72h urgency markup, %</span><input type="number" min="0" value={config.custom_pricing_urgency_72h_percent ?? 20} onChange={(event) => onConfigChange('custom_pricing_urgency_72h_percent', Number(event.target.value))} /></label>
          <label className="v2-field"><span>24h urgency markup, %</span><input type="number" min="0" value={config.custom_pricing_urgency_24h_percent ?? 40} onChange={(event) => onConfigChange('custom_pricing_urgency_24h_percent', Number(event.target.value))} /></label>
          <label className="v2-field"><span>Negotiation margin, %</span><input type="number" min="0" value={config.custom_pricing_negotiation_margin_percent ?? 15} onChange={(event) => onConfigChange('custom_pricing_negotiation_margin_percent', Number(event.target.value))} /></label>
          <label className="v2-field"><span>Minimum deposit, %</span><input type="number" min="50" max="100" value={config.custom_pricing_minimum_deposit_percent ?? 50} onChange={(event) => onConfigChange('custom_pricing_minimum_deposit_percent', Number(event.target.value))} /></label>
          <label className="v2-field"><span>Turnaround, days</span><input type="number" min="1" value={config.custom_standard_turnaround_days ?? 7} onChange={(event) => onConfigChange('custom_standard_turnaround_days', Number(event.target.value))} /></label>
          <label className="v2-field"><span>Max video, minutes</span><input type="number" min="1" value={config.custom_max_video_minutes ?? 10} onChange={(event) => onConfigChange('custom_max_video_minutes', Number(event.target.value))} /></label>
          <label className="v2-field"><span>Max audio, minutes</span><input type="number" min="1" value={config.custom_max_audio_minutes ?? 10} onChange={(event) => onConfigChange('custom_max_audio_minutes', Number(event.target.value))} /></label>
          <label className="v2-field"><span>Max photos</span><input type="number" min="1" value={config.custom_max_photo_count ?? 10} onChange={(event) => onConfigChange('custom_max_photo_count', Number(event.target.value))} /></label>
          <label className="v2-field"><span>External-item budget cap</span><input type="number" min="0" value={config.custom_max_purchase_budget ?? 0} onChange={(event) => onConfigChange('custom_max_purchase_budget', Number(event.target.value))} /></label>
        </div></> : <p>In handoff mode the bot collects the request and calls a person. It does not promise a price or production slot.</p>}
        <label className="v2-field"><span>Approved scenario ideas (one per line)</span><textarea rows="3" value={(config.custom_allowed_scenarios || []).join('\n')} onChange={(event) => onConfigChange('custom_allowed_scenarios', event.target.value.split('\n').map((item) => item.trim()).filter(Boolean))} /></label>
        <label className="v2-field"><span>Hard forbidden scenarios (one per line)</span><textarea rows="4" value={(config.custom_forbidden_scenarios || []).join('\n')} onChange={(event) => onConfigChange('custom_forbidden_scenarios', event.target.value.split('\n').map((item) => item.trim()).filter(Boolean))} /></label>
        <details className="v2-advanced-disclosure"><summary>Advanced production capabilities and inventory</summary><div className="v2-onboarding-toggle-grid"><DraftToggle label="Toy use" checked={config.custom_toy_use_allowed} onChange={(value) => onConfigChange('custom_toy_use_allowed', value)} /><DraftToggle label="Toy insertion" checked={config.custom_toy_insertion_allowed} onChange={(value) => onConfigChange('custom_toy_insertion_allowed', value)} /><DraftToggle label="Other-object insertion" checked={config.custom_other_object_insertion_allowed} onChange={(value) => onConfigChange('custom_other_object_insertion_allowed', value)} /><DraftToggle label="GG partner content" checked={config.custom_gg_content_allowed} onChange={(value) => onConfigChange('custom_gg_content_allowed', value)} /><DraftToggle label="BG partner content" checked={config.custom_bg_content_allowed} onChange={(value) => onConfigChange('custom_bg_content_allowed', value)} /><DraftToggle label="May source external items" checked={config.custom_external_items_allowed} onChange={(value) => onConfigChange('custom_external_items_allowed', value)} /><DraftToggle label="May buy an outfit" checked={config.custom_outfit_purchase_allowed} onChange={(value) => onConfigChange('custom_outfit_purchase_allowed', value)} /><DraftToggle label="May buy a prop" checked={config.custom_prop_purchase_allowed} onChange={(value) => onConfigChange('custom_prop_purchase_allowed', value)} /></div><div className="v2-form-grid">
          <label className="v2-field"><span>Available outfits (one per line)</span><textarea rows="3" value={(config.custom_available_outfits || []).join('\n')} onChange={(event) => onConfigChange('custom_available_outfits', event.target.value.split('\n').map((item) => item.trim()).filter(Boolean))} /></label>
          <label className="v2-field"><span>Available locations (one per line)</span><textarea rows="3" value={(config.custom_available_locations || []).join('\n')} onChange={(event) => onConfigChange('custom_available_locations', event.target.value.split('\n').map((item) => item.trim()).filter(Boolean))} /></label>
          <label className="v2-field"><span>Available props (one per line)</span><textarea rows="3" value={(config.custom_available_props || []).join('\n')} onChange={(event) => onConfigChange('custom_available_props', event.target.value.split('\n').map((item) => item.trim()).filter(Boolean))} /></label>
        </div></details>
      </div>
    )
  }
  if (routeKey === 'tips_support') {
    return (
      <div className="v2-onboarding-route-config">
        <div className="v2-onboarding-toggle-grid">
          <DraftToggle label="Post-experience appreciation" checked={config.support_afterglow_enabled} onChange={(value) => onConfigChange('support_afterglow_enabled', value)} />
          <DraftToggle label="Soft chat gate" checked={config.support_chat_gate_enabled} onChange={(value) => onConfigChange('support_chat_gate_enabled', value)} />
          <DraftToggle label="Approved support campaign" checked={config.support_campaign_enabled} onChange={(value) => onConfigChange('support_campaign_enabled', value)} />
          {config.support_chat_gate_enabled ? <DraftToggle label="Build toward the ask gradually" checked={config.support_chat_gate_gradual_ask_enabled} onChange={(value) => onConfigChange('support_chat_gate_gradual_ask_enabled', value)} /> : null}
          {config.support_chat_gate_enabled ? <DraftToggle label="Ask what amount feels fair" checked={config.support_chat_gate_amount_question_enabled} onChange={(value) => onConfigChange('support_chat_gate_amount_question_enabled', value)} /> : null}
        </div>
        {config.support_afterglow_enabled ? <div className="v2-form-grid"><label className="v2-field"><span>Afterglow minimum score</span><input type="number" min="50" max="100" value={config.support_afterglow_min_score ?? 75} onChange={(event) => onConfigChange('support_afterglow_min_score', Number(event.target.value))} /></label><label className="v2-field"><span>Afterglow cooldown, days</span><input type="number" min="1" value={config.support_afterglow_cooldown_days ?? 14} onChange={(event) => onConfigChange('support_afterglow_cooldown_days', Number(event.target.value))} /></label></div> : null}
        {config.support_chat_gate_enabled ? <div className="v2-form-grid"><label className="v2-field"><span>Minimum voluntary tip</span><input type="number" min="0.01" value={config.support_chat_gate_min_tip_amount ?? 5} onChange={(event) => onConfigChange('support_chat_gate_min_tip_amount', Number(event.target.value))} /></label><label className="v2-field"><span>Currency</span><select value={config.support_chat_gate_currency || 'USD'} onChange={(event) => onConfigChange('support_chat_gate_currency', event.target.value)}><option>USD</option><option>EUR</option><option>GBP</option></select></label><label className="v2-field"><span>Cooldown, days</span><input type="number" min="1" value={config.support_chat_gate_cooldown_days ?? 1} onChange={(event) => onConfigChange('support_chat_gate_cooldown_days', Number(event.target.value))} /></label><label className="v2-field"><span>Free-message limit</span><input type="number" min="4" value={config.support_chat_gate_message_limit ?? 12} onChange={(event) => onConfigChange('support_chat_gate_message_limit', Number(event.target.value))} /></label><label className="v2-field"><span>Reminder interval, messages</span><input type="number" min="2" value={config.support_chat_gate_reminder_interval_messages ?? 4} onChange={(event) => onConfigChange('support_chat_gate_reminder_interval_messages', Number(event.target.value))} /></label><label className="v2-field"><span>Maximum reminders</span><input type="number" min="0" max="10" value={config.support_chat_gate_max_reminders ?? 2} onChange={(event) => onConfigChange('support_chat_gate_max_reminders', Number(event.target.value))} /></label></div> : null}
        {config.support_campaign_enabled ? <><label className="v2-field"><span>Truthful, model-approved campaign reason</span><textarea rows="3" value={config.support_campaign_reason || ''} onChange={(event) => onConfigChange('support_campaign_reason', event.target.value)} /></label><div className="v2-form-grid"><label className="v2-field"><span>Campaign goal</span><input type="number" min="0" value={config.support_campaign_goal_amount ?? 0} onChange={(event) => onConfigChange('support_campaign_goal_amount', Number(event.target.value))} /></label><label className="v2-field"><span>Campaign currency</span><select value={config.support_campaign_currency || 'USD'} onChange={(event) => onConfigChange('support_campaign_currency', event.target.value)}><option>USD</option><option>EUR</option><option>GBP</option></select></label><label className="v2-field"><span>Campaign cooldown, days</span><input type="number" min="1" value={config.support_campaign_cooldown_days ?? 14} onChange={(event) => onConfigChange('support_campaign_cooldown_days', Number(event.target.value))} /></label></div></> : null}
      </div>
    )
  }
  if (routeKey === 'wishlist') {
    return (
      <div className="v2-onboarding-route-config">
        <label className="v2-field"><span>Verified public HTTPS wishlist</span><input type="url" value={config.support_wishlist_url || ''} onChange={(event) => onConfigChange('support_wishlist_url', event.target.value)} /></label>
        <DraftToggle label="The link is present on the model profile" checked={config.support_wishlist_profile_linked} onChange={(value) => onConfigChange('support_wishlist_profile_linked', value)} />
      </div>
    )
  }
  if (routeKey === 'physical_items') {
    return (
      <div className="v2-onboarding-route-config">
        <div className="v2-form-grid">
          <label className="v2-field"><span>Currency</span><select value={config.physical_item_currency || 'USD'} onChange={(event) => onConfigChange('physical_item_currency', event.target.value)}><option>USD</option><option>EUR</option><option>GBP</option></select></label>
          <label className="v2-field"><span>Underwear from</span><input type="number" min="0" value={config.physical_item_underwear_starting_price ?? 75} onChange={(event) => onConfigChange('physical_item_underwear_starting_price', Number(event.target.value))} /></label>
          <label className="v2-field"><span>Clothing from</span><input type="number" min="0" value={config.physical_item_clothing_starting_price ?? 90} onChange={(event) => onConfigChange('physical_item_clothing_starting_price', Number(event.target.value))} /></label>
          <label className="v2-field"><span>Other items from</span><input type="number" min="0" value={config.physical_item_other_starting_price ?? 60} onChange={(event) => onConfigChange('physical_item_other_starting_price', Number(event.target.value))} /></label>
          <label className="v2-field"><span>Worn-day markup</span><input type="number" min="0" value={config.physical_item_worn_day_markup ?? 15} onChange={(event) => onConfigChange('physical_item_worn_day_markup', Number(event.target.value))} /></label>
          <label className="v2-field"><span>Personalization markup</span><input type="number" min="0" value={config.physical_item_personalization_markup ?? 15} onChange={(event) => onConfigChange('physical_item_personalization_markup', Number(event.target.value))} /></label>
        </div>
        <p>Shipping, availability and fulfilment remain a human decision.</p>
      </div>
    )
  }
  return null
}

export default function AdminV2Onboarding() {
  const { models, loading, selectedModelId, setSelectedModelId, refreshModels } = useWorkspaceScope()
  const [searchParams, setSearchParams] = useSearchParams()
  const [refreshError, setRefreshError] = useState('')
  const [creating, setCreating] = useState(false)
  const requestedId = searchParams.get('model_id')
  const modelId = resolveOnboardingModelId(models, selectedModelId, requestedId)

  // Deep links are validated against the API's accessible models, then consumed.
  // Later changes to the global selector must take precedence over an old URL.
  useEffect(() => {
    if (loading || !modelId) return
    if (modelId !== selectedModelId) setSelectedModelId(modelId)
    if (requestedId) {
      const next = new URLSearchParams(searchParams)
      next.delete('model_id')
      setSearchParams(next, { replace: true })
    }
  }, [loading, modelId, selectedModelId, requestedId, searchParams])

  async function reloadModels() {
    setRefreshError('')
    try { await refreshModels() } catch (err) { setRefreshError(getApiErrorMessage(err, 'Failed to refresh models')) }
  }

  async function modelCreated(model) {
    await refreshModels()
    setSelectedModelId(String(model.id))
    setSearchParams({ model_id: String(model.id) }, { replace: true })
    setCreating(false)
  }

  if (loading) return <LoadingBlock label="Loading onboarding workspace..." />
  const creation = <CreateModelWorkspace onCreated={modelCreated} onCancel={modelId ? () => setCreating(false) : undefined} />
  if (!modelId) return <div className="v2-stack">
    {refreshError ? <div className="v2-error" role="alert">{refreshError}</div> : null}
    <EmptyState title={requestedId ? 'No access to the requested model' : 'Start with your first model'} description="Create a paused workspace below or choose a model already assigned to your account."
      action={<button type="button" className="v2-btn v2-btn-secondary" onClick={requestedId ? () => setSearchParams({}, { replace: true }) : reloadModels}>{requestedId ? 'Choose an accessible model' : 'Refresh models'}</button>} />
    {creation}
  </div>
  // A fresh workspace prevents pending requests, credentials and unsaved drafts
  // for one model from being displayed or saved under another model's selector.
  return <div className="v2-stack">
    <div className="v2-toolbar"><button type="button" className="v2-btn v2-btn-secondary" onClick={() => setCreating((value) => !value)}>{creating ? 'Close new model form' : '+ Add new model'}</button></div>
    {creating ? creation : null}
    <OnboardingWorkspace key={modelId} models={models} modelId={modelId} onModelChange={setSelectedModelId} onModelsRefresh={refreshModels} />
  </div>
}

function OnboardingWorkspace({ models, modelId, onModelChange, onModelsRefresh }) {
  const [sessions, setSessions] = useState([])
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState('')
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [auditBucket, setAuditBucket] = useState('fix')
  const [sourceForm, setSourceForm] = useState({
    source_type: 'brief',
    name: 'Model brief',
    authority: 'human_confirmed',
    retention_mode: 'until_publish',
    content: '',
  })
  const [sourceFile, setSourceFile] = useState(null)
  const sourceFileInput = useRef(null)
  const [historyForm, setHistoryForm] = useState({
    days: 30,
    max_conversations: 20,
    min_messages: 6,
  })
  const [editingCandidate, setEditingCandidate] = useState(null)
  const [candidateText, setCandidateText] = useState('')
  const [draftSection, setDraftSection] = useState('persona')
  const [draftText, setDraftText] = useState('')
  const [releaseLabel, setReleaseLabel] = useState('')
  const [ackWarnings, setAckWarnings] = useState(false)
  const [findingNotes, setFindingNotes] = useState({})
  const [monetizationPlan, setMonetizationPlan] = useState(EMPTY_MONETIZATION_PLAN)
  const [monetizationDirty, setMonetizationDirty] = useState(false)
  const [activeWizardStep, setActiveWizardStep] = useState('connection')
  const [behaviorPlan, setBehaviorPlan] = useState(EMPTY_BEHAVIOR_PLAN)
  const [behaviorDirty, setBehaviorDirty] = useState(false)
  const [connectionPlatforms, setConnectionPlatforms] = useState([])
  const [onlyFansConnector, setOnlyFansConnector] = useState('onlyfansapi')
  const [onlyFansConnection, setOnlyFansConnection] = useState({
    apiKey: '', accountId: '', connectionId: '', apiToken: '', platformAccountId: '', username: '',
  })
  const [fanslyConnection, setFanslyConnection] = useState({ apiKey: '', accountId: '', accounts: [] })
  const [fanvueConnection, setFanvueConnection] = useState({ authUrl: '', state: '' })
  const sessionRequest = useRef(0)

  const selectedModel = useMemo(
    () => models.find((item) => String(item.id) === String(modelId)),
    [modelId, models],
  )

  async function loadSession(sessionId, { silent = false } = {}) {
    if (!sessionId) return
    const request = ++sessionRequest.current
    if (!silent) setLoading(true)
    try {
      const result = await getOnboardingSession(sessionId)
      if (request !== sessionRequest.current) return
      if (String(result.model_id) !== String(modelId)) throw new Error('Onboarding model mismatch. Reload the workspace.')
      setSession(result)
      setSessions((current) => current.map((item) => (item.id === result.id ? result : item)))
    } catch (err) {
      if (request === sessionRequest.current) setError(getApiErrorMessage(err, 'Failed to refresh onboarding'))
    } finally {
      if (!silent && request === sessionRequest.current) setLoading(false)
    }
  }

  async function loadSessions(nextModelId) {
    const request = ++sessionRequest.current
    if (!nextModelId) {
      setSessions([])
      setSession(null)
      return
    }
    setLoading(true)
    setError('')
    try {
      const result = asList(await listOnboardingSessions(nextModelId))
      if (request !== sessionRequest.current) return
      if (result.some((row) => String(row.model_id) !== String(modelId))) throw new Error('Onboarding model mismatch. Reload the workspace.')
      setSessions(result)
      setSession(result[0] || null)
    } catch (err) {
      if (request !== sessionRequest.current) return
      setError(getApiErrorMessage(err, 'Failed to load onboarding sessions'))
      setSessions([])
      setSession(null)
    } finally {
      if (request === sessionRequest.current) setLoading(false)
    }
  }

  useEffect(() => {
    if (modelId) loadSessions(modelId)
  }, [modelId])

  useEffect(() => {
    const behaviorStatus = session?.releases?.[0]?.test_summary?.behavior_execution?.status
    const shouldPoll = session?.status === 'analyzing' || behaviorStatus === 'queued' || behaviorStatus === 'running'
    if (!session || !shouldPoll) return undefined
    const timer = window.setInterval(() => loadSession(session.id, { silent: true }), 3500)
    return () => window.clearInterval(timer)
  }, [session?.id, session?.status, session?.releases?.[0]?.test_summary?.behavior_execution?.status])

  useEffect(() => {
    const value = session?.draft_sections?.[draftSection]
    setDraftText(value == null ? '' : displayValue(value))
  }, [draftSection, session?.id, session?.state_version])

  useEffect(() => {
    setAckWarnings(false)
  }, [session?.releases?.[0]?.id])

  useEffect(() => {
    setFindingNotes({})
  }, [session?.id])

  useEffect(() => {
    const next = session?.settings?.monetization_plan || EMPTY_MONETIZATION_PLAN
    setMonetizationPlan(JSON.parse(JSON.stringify(next)))
    setMonetizationDirty(false)
  }, [session?.id, session?.state_version])

  useEffect(() => {
    const next = session?.settings?.behavior_plan || EMPTY_BEHAVIOR_PLAN
    setBehaviorPlan({ ...EMPTY_BEHAVIOR_PLAN, ...JSON.parse(JSON.stringify(next)) })
    setBehaviorDirty(false)
  }, [session?.id, session?.state_version])

  useEffect(() => {
    if (!session?.id) return
    const requested = session.current_step === 'content'
      ? 'monetization'
      : ['shadow', 'pilot', 'publish'].includes(session.current_step)
        ? 'test'
        : session.current_step
    if (STEPS.some(([key]) => key === requested)) setActiveWizardStep(requested)
  }, [session?.id])

  useEffect(() => {
    const setup = session?.settings?.connection_setup || {}
    const savedPlatforms = Array.isArray(setup.platforms) ? setup.platforms : []
    const inferredPlatforms = asList(session?.readiness?.checks?.providers).map((item) => {
      if (['onlyfansapi', 'onlymonster', 'fanltv_ofapi'].includes(item.provider)) return 'onlyfans'
      if (item.provider === 'fansly') return 'fansly'
      if (item.provider === 'fanvue') return 'fanvue'
      return null
    }).filter(Boolean)
    setConnectionPlatforms(savedPlatforms.length ? savedPlatforms : Array.from(new Set(inferredPlatforms)))
    setOnlyFansConnector(setup.onlyfans_connector === 'onlymonster' ? 'onlymonster' : 'onlyfansapi')
  }, [session?.id])

  async function runAction(key, action, success, { refresh = true } = {}) {
    if (!session && key !== 'start') return
    setBusy(key)
    setError('')
    setNotice('')
    try {
      const result = await action()
      if (refresh && session?.id) await loadSession(session.id, { silent: true })
      if (success) setNotice(success)
      return result
    } catch (err) {
      setError(getApiErrorMessage(err, 'Onboarding action failed'))
      return null
    } finally {
      setBusy('')
    }
  }

  function toggleConnectionPlatform(platform, enabled) {
    setConnectionPlatforms((current) => (
      enabled
        ? Array.from(new Set([...current, platform]))
        : current.filter((item) => item !== platform)
    ))
  }

  async function saveConnectionSelection() {
    const result = await runAction(
      'save-connection-selection',
      () => updateOnboardingSession(session.id, {
        settings: {
          connection_setup: {
            platforms: connectionPlatforms,
            onlyfans_connector: onlyFansConnector,
          },
        },
        expected_state_version: session.state_version,
      }),
      'Platform selection saved. Add the credentials below.',
      { refresh: false },
    )
    if (result) setSession(result)
  }

  async function finishProviderConnection(updatedModel, message) {
    if (updatedModel?.id) await onModelsRefresh()
    await evaluateOnboardingReadiness(session.id)
    await loadSession(session.id, { silent: true })
    setNotice(message)
  }

  async function connectOnlyFans(event) {
    event.preventDefault()
    setBusy('connect-onlyfans')
    setError('')
    setNotice('')
    try {
      let updated
      if (onlyFansConnector === 'onlymonster') {
        updated = await connectModelOnlyMonsterAccount(modelId, {
          apiToken: onlyFansConnection.apiToken.trim() || undefined,
          accountId: onlyFansConnection.accountId.trim() || undefined,
          platformAccountId: onlyFansConnection.platformAccountId.trim() || undefined,
          username: onlyFansConnection.username.trim() || undefined,
        })
      } else {
        updated = await connectModelOnlyfansApiAccount(modelId, {
          apiKey: onlyFansConnection.apiKey.trim() || undefined,
          accountId: onlyFansConnection.accountId.trim() || undefined,
          connectionId: onlyFansConnection.connectionId.trim() || undefined,
        })
      }
      setOnlyFansConnection({ apiKey: '', accountId: '', connectionId: '', apiToken: '', platformAccountId: '', username: '' })
      await finishProviderConnection(updated, `OnlyFans connected through ${onlyFansConnector === 'onlymonster' ? 'OnlyMonster' : 'OnlyFansAPI'}.`)
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to connect OnlyFans'))
    } finally {
      setBusy('')
    }
  }

  async function loadFanslyAccounts(event) {
    event.preventDefault()
    setBusy('fansly-accounts')
    setError('')
    try {
      const result = await listFanslyAuthAccounts(modelId, {
        apiKey: fanslyConnection.apiKey.trim() || undefined,
      })
      const accounts = asList(result?.accounts)
      setFanslyConnection((current) => ({
        ...current,
        accounts,
        accountId: accounts.length === 1 ? accounts[0].id : current.accountId,
      }))
      setNotice(accounts.length ? `Found ${accounts.length} Fansly account${accounts.length === 1 ? '' : 's'}. Select the account to connect.` : 'The key is valid, but no Fansly accounts were returned.')
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to validate Fansly API key'))
    } finally {
      setBusy('')
    }
  }

  async function connectFansly(event) {
    event.preventDefault()
    if (!fanslyConnection.accountId) return
    setBusy('connect-fansly')
    setError('')
    setNotice('')
    try {
      const account = fanslyConnection.accounts.find((item) => String(item.id) === String(fanslyConnection.accountId)) || {}
      const updated = await connectModelFanslyAccount(modelId, {
        apiKey: fanslyConnection.apiKey.trim() || undefined,
        accountId: String(fanslyConnection.accountId),
        fanslyId: account.fansly_id || account.fanslyId || undefined,
        username: account.fansly_username || account.username || undefined,
      })
      setFanslyConnection({ apiKey: '', accountId: '', accounts: [] })
      await finishProviderConnection(updated, 'Fansly account connected.')
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to connect Fansly account'))
    } finally {
      setBusy('')
    }
  }

  async function connectFanvue() {
    const popup = window.open('', '_blank')
    if (popup) {
      try { popup.document.write('<!doctype html><title>Fanvue OAuth</title><p>Opening Fanvue OAuth...</p>') } catch (_) {}
    }
    setBusy('connect-fanvue')
    setError('')
    setNotice('')
    try {
      const result = await startFanvuePlatformOAuth(modelId)
      const authUrl = result?.auth_url || result?.authUrl || ''
      const state = result?.state || ''
      rememberFanvueOAuthSession({ state, modelId, authUrl })
      setFanvueConnection({ authUrl, state })
      if (authUrl && popup && !popup.closed) {
        try { popup.opener = null } catch (_) {}
        popup.location.href = authUrl
        setNotice('Fanvue OAuth opened in a new tab. Return here after approving access and check the connection.')
      } else if (authUrl) {
        if (popup && !popup.closed) popup.close()
        setNotice('OAuth link created. Open it below to approve Fanvue access.')
      } else {
        if (popup && !popup.closed) popup.close()
        setNotice('Fanvue OAuth session created. Complete the callback to finish the connection.')
      }
    } catch (err) {
      if (popup && !popup.closed) popup.close()
      setError(getApiErrorMessage(err, 'Failed to start Fanvue OAuth'))
    } finally {
      setBusy('')
    }
  }

  async function handleStart() {
    const request = ++sessionRequest.current
    setBusy('start')
    setError('')
    try {
      const result = await startOnboardingSession(Number(modelId))
      if (request !== sessionRequest.current) return
      if (String(result.model_id) !== String(modelId)) throw new Error('Onboarding model mismatch. Reload the workspace.')
      setSession(result)
      setSessions((current) => [result, ...current.filter((item) => item.id !== result.id)])
      setNotice('Onboarding workspace is ready. Live automation has not changed.')
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to start onboarding'))
    } finally {
      setBusy('')
    }
  }

  async function handleCancel() {
    if (!window.confirm('Cancel this onboarding workspace? Production configuration and bot state will not change.')) return
    const result = await runAction(
      'cancel-session',
      () => cancelOnboardingSession(session.id, session.state_version),
      'Onboarding workspace cancelled. Production was not changed.',
      { refresh: false },
    )
    if (result) {
      setSession(result)
      setSessions((current) => current.map((item) => (item.id === result.id ? result : item)))
    }
  }

  async function handleAddSource(event) {
    event.preventDefault()
    if (!sourceForm.content.trim()) return
    const result = await runAction(
      'add-source',
      () => addOnboardingSource(session.id, sourceForm),
      'Source added to the isolated draft workspace.',
    )
    if (result) setSourceForm((current) => ({ ...current, content: '' }))
  }

  async function handleImportHistory() {
    const result = await runAction(
      'import-history',
      () => importOnboardingHistory(session.id, historyForm),
      '',
    )
    if (result) {
      setNotice(`Imported ${result.created} conversations; ${result.existing} were already present.`)
    }
  }

  async function handleUploadSource() {
    if (!sourceFile) return
    const result = await runAction(
      'upload-source',
      () => uploadOnboardingSource(session.id, {
        file: sourceFile,
        name: sourceFile.name,
        source_type: sourceForm.source_type === 'historical_chat' ? 'document' : sourceForm.source_type,
        authority: sourceForm.authority,
        retention_mode: sourceForm.retention_mode,
      }),
      'Document text extracted into the isolated draft workspace; the binary file was not retained.',
    )
    if (result) {
      setSourceFile(null)
      if (sourceFileInput.current) sourceFileInput.current.value = ''
    }
  }

  async function handleRemoveSource(source) {
    const isPending = source.status === 'pending'
    const prompt = isPending
      ? `Remove “${source.name}” from this draft workspace?`
      : `Exclude “${source.name}” from future analysis and releases? Its audit history will remain.`
    if (!window.confirm(prompt)) return
    await runAction(
      `remove-${source.id}`,
      () => removeOnboardingSource(session.id, source.id),
      isPending
        ? 'Source removed.'
        : 'Source excluded; its audit trail remains, but it no longer affects the draft or release.',
    )
  }

  async function handleCandidate(candidate, status, payload) {
    const result = await runAction(
      `candidate-${candidate.id}`,
      () => reviewOnboardingCandidate(session.id, candidate.id, {
        status,
        payload,
        expected_state_version: session.state_version,
      }),
      'Review decision saved.',
      { refresh: false },
    )
    if (result) setSession(result)
  }

  async function saveCandidateEdit(candidate) {
    const payload = {
      ...(candidate.payload || {}),
      value: parseEditorValue(candidateText),
    }
    await handleCandidate(candidate, 'edited', payload)
    setEditingCandidate(null)
    setCandidateText('')
  }

  async function saveDraft() {
    const result = await runAction(
      'save-draft',
      () => updateOnboardingDraft(session.id, {
        section: draftSection,
        value: parseEditorValue(draftText),
        expected_state_version: session.state_version,
      }),
      `${draftSection.replaceAll('_', ' ')} saved to the draft.`,
      { refresh: false },
    )
    if (result) setSession(result)
  }

  function updateMonetizationRoute(routeKey, patch) {
    setMonetizationDirty(true)
    setMonetizationPlan((current) => ({
      ...current,
      confirmed: false,
      routes: {
        ...(current.routes || {}),
        [routeKey]: {
          ...(current.routes?.[routeKey] || {}),
          ...patch,
        },
      },
    }))
  }

  function updateMonetizationConfig(routeKey, key, value) {
    const route = monetizationPlan.routes?.[routeKey] || {}
    updateMonetizationRoute(routeKey, {
      config: { ...(route.config || {}), [key]: value },
    })
  }

  async function saveMonetizationPlan() {
    const result = await runAction(
      'save-monetization',
      () => updateOnboardingMonetization(session.id, {
        plan: monetizationPlan,
        expected_state_version: session.state_version,
      }),
      'Monetization routes saved to the isolated onboarding draft.',
      { refresh: false },
    )
    if (result) setSession(result)
  }

  function updateBehavior(key, value) {
    setBehaviorDirty(true)
    setBehaviorPlan((current) => ({ ...current, confirmed: false, [key]: value }))
  }

  async function saveBehaviorPlan() {
    const result = await runAction(
      'save-behavior',
      () => updateOnboardingSession(session.id, {
        settings: { behavior_plan: behaviorPlan },
        expected_state_version: session.state_version,
      }),
      'Conversation behavior saved to the isolated onboarding draft.',
      { refresh: false },
    )
    if (result) setSession(result)
  }

  function moveWizard(offset) {
    const index = STEPS.findIndex(([key]) => key === activeWizardStep)
    const next = STEPS[Math.max(0, Math.min(STEPS.length - 1, index + offset))]
    if (next) {
      setActiveWizardStep(next[0])
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  async function handleFindingReview(finding, status) {
    const note = String(findingNotes[finding.id] || '').trim()
    const result = await runAction(
      `finding-${finding.id}`,
      () => reviewOnboardingFinding(session.id, finding.id, {
        status,
        note: note || undefined,
        expected_state_version: session.state_version,
      }),
      status === 'accepted_risk' ? 'Risk decision recorded.' : 'Finding marked resolved.',
      { refresh: false },
    )
    if (result) {
      setSession(result)
      setFindingNotes((current) => {
        const next = { ...current }
        delete next[finding.id]
        return next
      })
    }
  }

  async function handlePublishRelease() {
    if (!window.confirm(`Publish release v${latestRelease.version} for ${modelTitle(selectedModel)}? This changes the production configuration but preserves the current bot pause state.`)) return
    await runAction(
      'publish-release',
      () => publishOnboardingRelease(session.id, latestRelease.id, {
        expected_snapshot_hash: latestRelease.snapshot_hash,
        acknowledge_warnings: ackWarnings,
      }),
      'Release published. Existing bot pause state was preserved.',
    )
  }

  async function handleRollbackRelease() {
    if (!window.confirm(`Roll back release v${latestRelease.version} for ${modelTitle(selectedModel)} to its recorded previous configuration?`)) return
    await runAction(
      'rollback-release',
      () => rollbackOnboardingRelease(session.id, latestRelease.id),
      'Release rolled back.',
    )
  }

  if (loading) return <LoadingBlock label="Loading onboarding workspace..." />

  const candidates = session?.candidates || []
  const findings = session?.findings || []
  const releases = session?.releases || []
  const latestRelease = releases[0]
  const readiness = session?.readiness || {}
  const readinessIsCurrent = Number(readiness.session_state_version || 0) === Number(session?.state_version || 0)
  const releaseReady = Boolean(readiness.ready && readinessIsCurrent)
  const bucketFindings = findings.filter((item) => item.bucket === auditBucket)
  const pendingCandidates = countByStatus(candidates, 'pending')
  const openStops = findings.filter(
    (item) => item.bucket === 'stop' && item.status === 'open' && ['high', 'critical'].includes(item.severity),
  ).length
  const isTerminalSession = ['published', 'rolled_back', 'cancelled'].includes(session?.status)
  const activeSources = (session?.sources || []).filter((item) => item.status !== 'excluded')
  const failedTestWarnings = (latestRelease?.test_summary?.warnings || []).filter(
    (item) => item && item.passed === false,
  )
  const requiresWarningAck = Boolean(
    (readiness.warnings || []).length || failedTestWarnings.length,
  )
  const readinessHasRun = Number(readiness.session_state_version || 0) > 0
  const monetizationReadiness = readiness.checks?.monetization || {}
  const connectionChecks = readiness.checks || {}
  const connectedProviders = Array.isArray(connectionChecks.providers) ? connectionChecks.providers : []
  const routeReadiness = Object.fromEntries(
    (monetizationReadiness.routes || []).map((item) => [item.route, item]),
  )
  const contentInventory = readiness.checks?.media || {}
  const readinessLabel = !readinessHasRun
    ? 'Not checked'
    : !readinessIsCurrent
      ? 'Stale'
      : releaseReady
        ? 'Ready'
        : 'Blocked'
  const workflowCurrentStep = isTerminalSession
    ? 'publish'
    : session?.step_status?.connection?.status === 'passed'
      ? session.current_step
      : 'connection'

  return (
    <div className="v2-stack v2-onboarding-page">
      <section className="v2-onboarding-hero">
        <div>
          <p className="v2-kicker">Release onboarding</p>
          <h2>Set up and test this model</h2>
          <p>Sources, AI proposals and historical chats stay in a draft workspace. Publishing never resumes a paused bot.</p>
        </div>
        <label className="v2-field v2-onboarding-model-picker">
          <span>Model</span>
          <select value={modelId} onChange={(event) => onModelChange(event.target.value)}>
            {models.map((model) => <option key={model.id} value={model.id}>{modelTitle(model)}</option>)}
          </select>
        </label>
      </section>

      {error ? <div className="v2-error" role="alert">{error}</div> : null}
      {notice ? <div className="v2-info" role="status">{notice}</div> : null}

      {!session ? (
        <Section
          eyebrow="Workspace"
          title={`Start onboarding for ${modelTitle(selectedModel)}`}
          description="This creates an isolated draft. Current live prompts, prices, bot state and automation mode remain unchanged."
        >
          <EmptyState
            title="No onboarding session yet"
            description="Start with a brief, existing documents, or a bounded import of historical conversations."
            action={<button type="button" className="v2-btn v2-btn-primary" disabled={busy === 'start'} onClick={handleStart}>{busy === 'start' ? 'Creating...' : 'Start onboarding'}</button>}
          />
        </Section>
      ) : (
        <>
          <div className="v2-metrics-grid">
            <MetricCard label="Draft revision" value={`v${session.state_version}`} detail="Protects against stale edits" />
            <MetricCard label="Active sources" value={activeSources.length} detail={`${activeSources.filter((item) => item.status === 'analyzed').length} analyzed · ${session.sources.length - activeSources.length} excluded`} />
            <MetricCard label="Review queue" value={pendingCandidates} detail="AI proposals need a human decision" tone={pendingCandidates ? 'warn' : 'good'} />
            <MetricCard label="Release gate" value={readinessLabel} detail={`${readiness.score ?? 0}% of release checks passed`} tone={releaseReady ? 'good' : 'warn'} />
          </div>

          <div className="v2-onboarding-session-bar">
            <div>
              <strong>{modelTitle(selectedModel)}</strong>
              <span>Session {session.id.slice(0, 8)} · created {formatDate(session.created_at)}</span>
            </div>
            <div className="v2-action-row">
              <StatusPill tone={statusTone(session.status)}>{session.status}</StatusPill>
              {sessions.length > 1 ? (
                <select aria-label="Onboarding session" value={session.id} disabled={!!busy} onChange={(event) => loadSession(event.target.value)}>
                  {sessions.map((item) => <option key={item.id} value={item.id}>Session {item.id.slice(0, 8)} · {item.status}</option>)}
                </select>
              ) : null}
              <button type="button" className="v2-btn v2-btn-ghost" disabled={!!busy} onClick={() => loadSession(session.id)}>Refresh</button>
              {!isTerminalSession ? <button type="button" className="v2-btn v2-btn-ghost" disabled={!!busy} onClick={handleCancel}>{busy === 'cancel-session' ? 'Cancelling...' : 'Cancel workspace'}</button> : null}
              {isTerminalSession ? <button type="button" className="v2-btn v2-btn-secondary" disabled={!!busy} onClick={handleStart}>Start new onboarding</button> : null}
            </div>
          </div>

          {session.error ? <div className="v2-error" role="alert">{session.error}</div> : null}

          <div className="v2-onboarding-wizard-layout">
          <ol className="v2-onboarding-steps" aria-label="Onboarding progress">
            {STEPS.map(([key, label]) => {
              const value = session.step_status?.[key]?.status || 'not_started'
              return (
                <li key={key} className={`${key === activeWizardStep ? 'is-current' : ''} ${key === workflowCurrentStep ? 'is-recommended' : ''}`.trim()}>
                  <button type="button" aria-current={key === activeWizardStep ? 'step' : undefined} onClick={() => setActiveWizardStep(key)}>
                  <StatusPill tone={statusTone(value)}>{value.replaceAll('_', ' ')}</StatusPill>
                  <strong>{label}</strong>
                  </button>
                </li>
              )
            })}
           </ol>

          <main className="v2-onboarding-wizard-main">
            <div className="v2-onboarding-step-intro">
              <div><span>Step {STEPS.findIndex(([key]) => key === activeWizardStep) + 1} of {STEPS.length}</span><h2>{STEPS.find(([key]) => key === activeWizardStep)?.[1]}</h2></div>
              <StatusPill tone={STEP_HELP[activeWizardStep]?.required ? 'warn' : 'neutral'}>{STEP_HELP[activeWizardStep]?.required ? 'required before launch' : 'optional'}</StatusPill>
              <p><strong>What you need:</strong> {STEP_HELP[activeWizardStep]?.need}</p>
              <p><strong>Why it matters:</strong> {STEP_HELP[activeWizardStep]?.result}</p>
            </div>

          {activeWizardStep === 'connection' ? <Section
            eyebrow="1 · Connect"
            title="Choose the platforms and connect their accounts"
            description="Start with the channels this model will use. Credentials are sent directly to the existing encrypted connection layer and are never stored in the onboarding draft."
          >
            <div className="v2-onboarding-connect-flow">
              <div className="v2-onboarding-card">
                <div className="v2-onboarding-review-head">
                  <div><span>First</span><h3>Select platforms</h3></div>
                  <StatusPill tone={connectionPlatforms.length ? 'good' : 'warn'}>{connectionPlatforms.length ? `${connectionPlatforms.length} selected` : 'required'}</StatusPill>
                </div>
                <div className="v2-onboarding-platform-grid">
                  {CONNECTION_PLATFORMS.map((platform) => (
                    <label key={platform.key} className={`v2-onboarding-platform-choice ${connectionPlatforms.includes(platform.key) ? 'is-selected' : ''}`.trim()}>
                      <input type="checkbox" checked={connectionPlatforms.includes(platform.key)} disabled={isTerminalSession} onChange={(event) => toggleConnectionPlatform(platform.key, event.target.checked)} />
                      <span><strong>{platform.name}</strong><small>{platform.detail}</small></span>
                    </label>
                  ))}
                </div>
                <button type="button" className="v2-btn v2-btn-secondary" disabled={!!busy || !connectionPlatforms.length || isTerminalSession} onClick={saveConnectionSelection}>{busy === 'save-connection-selection' ? 'Saving...' : 'Save platform selection'}</button>
              </div>

              {connectionPlatforms.length ? (
                <div className="v2-onboarding-card">
                  <div><span>Then</span><h3>Connect selected accounts</h3></div>
                  {connectionPlatforms.includes('onlyfans') ? (
                    <form className="v2-onboarding-provider-form" onSubmit={connectOnlyFans}>
                      <div className="v2-onboarding-review-head"><strong>OnlyFans</strong><StatusPill tone={connectedProviders.some((item) => ['onlyfansapi', 'onlymonster', 'fanltv_ofapi'].includes(item.provider)) ? 'good' : 'warn'}>{connectedProviders.some((item) => ['onlyfansapi', 'onlymonster', 'fanltv_ofapi'].includes(item.provider)) ? 'connected' : 'not connected'}</StatusPill></div>
                      <div className="v2-onboarding-connector-choice">
                        <label><input type="radio" name="onlyfans-connector" value="onlyfansapi" checked={onlyFansConnector === 'onlyfansapi'} onChange={() => setOnlyFansConnector('onlyfansapi')} /> OnlyFansAPI</label>
                        <label><input type="radio" name="onlyfans-connector" value="onlymonster" checked={onlyFansConnector === 'onlymonster'} onChange={() => setOnlyFansConnector('onlymonster')} /> OnlyMonster</label>
                      </div>
                      <div className="v2-form-grid">
                        <label className="v2-field"><span>{onlyFansConnector === 'onlymonster' ? 'API token' : 'API key'}</span><input type="password" autoComplete="new-password" value={onlyFansConnector === 'onlymonster' ? onlyFansConnection.apiToken : onlyFansConnection.apiKey} onChange={(event) => setOnlyFansConnection((current) => ({ ...current, [onlyFansConnector === 'onlymonster' ? 'apiToken' : 'apiKey']: event.target.value }))} placeholder={onlyFansConnector === 'onlymonster' ? 'OnlyMonster API token' : 'ofapi_…'} /></label>
                        <label className="v2-field"><span>Account ID</span><input value={onlyFansConnection.accountId} onChange={(event) => setOnlyFansConnection((current) => ({ ...current, accountId: event.target.value }))} placeholder="Optional when the key can auto-detect it" /></label>
                        {onlyFansConnector === 'onlyfansapi' ? <label className="v2-field"><span>Connection ID</span><input value={onlyFansConnection.connectionId} onChange={(event) => setOnlyFansConnection((current) => ({ ...current, connectionId: event.target.value }))} placeholder="Optional" /></label> : null}
                        {onlyFansConnector === 'onlymonster' ? <label className="v2-field"><span>OnlyFans account ID</span><input value={onlyFansConnection.platformAccountId} onChange={(event) => setOnlyFansConnection((current) => ({ ...current, platformAccountId: event.target.value }))} placeholder="Optional platform account id" /></label> : null}
                      </div>
                      <button type="submit" className="v2-btn v2-btn-primary" disabled={!!busy || isTerminalSession || !(onlyFansConnector === 'onlymonster' ? (onlyFansConnection.apiToken.trim() || onlyFansConnection.accountId.trim()) : (onlyFansConnection.apiKey.trim() || onlyFansConnection.accountId.trim()))}>{busy === 'connect-onlyfans' ? 'Connecting...' : `Connect through ${onlyFansConnector === 'onlymonster' ? 'OnlyMonster' : 'OnlyFansAPI'}`}</button>
                    </form>
                  ) : null}

                  {connectionPlatforms.includes('fansly') ? (
                    <form className="v2-onboarding-provider-form" onSubmit={fanslyConnection.accounts.length ? connectFansly : loadFanslyAccounts}>
                      <div className="v2-onboarding-review-head"><strong>Fansly</strong><StatusPill tone={connectedProviders.some((item) => item.provider === 'fansly') ? 'good' : 'warn'}>{connectedProviders.some((item) => item.provider === 'fansly') ? 'connected' : 'not connected'}</StatusPill></div>
                      <div className="v2-form-grid">
                        <label className="v2-field"><span>OnlyFansAPI key</span><input type="password" autoComplete="new-password" value={fanslyConnection.apiKey} onChange={(event) => setFanslyConnection((current) => ({ ...current, apiKey: event.target.value, accounts: [], accountId: '' }))} placeholder="ofapi_… or leave blank to use the server key" /></label>
                        {fanslyConnection.accounts.length ? <label className="v2-field"><span>Fansly account</span><select value={fanslyConnection.accountId} onChange={(event) => setFanslyConnection((current) => ({ ...current, accountId: event.target.value }))}><option value="">Select account</option>{fanslyConnection.accounts.map((account) => <option key={account.id} value={account.id}>{account.display_name || account.fansly_username || account.id} · {account.is_authenticated ? 'authenticated' : (account.status || 'authentication required')}</option>)}</select></label> : null}
                      </div>
                      <button type="submit" className="v2-btn v2-btn-primary" disabled={!!busy || isTerminalSession || (fanslyConnection.accounts.length ? !fanslyConnection.accountId : false)}>{busy === 'fansly-accounts' ? 'Checking...' : busy === 'connect-fansly' ? 'Connecting...' : fanslyConnection.accounts.length ? 'Connect selected Fansly account' : 'Check key and load accounts'}</button>
                      {fanslyConnection.accounts.some((account) => String(account.id) === String(fanslyConnection.accountId) && !account.is_authenticated) ? <small>This account still needs authentication. Use Advanced connections to complete password or 2FA.</small> : null}
                    </form>
                  ) : null}

                  {connectionPlatforms.includes('fanvue') ? (
                    <div className="v2-onboarding-provider-form">
                      <div className="v2-onboarding-review-head"><strong>Fanvue</strong><StatusPill tone={connectedProviders.some((item) => item.provider === 'fanvue') ? 'good' : 'warn'}>{connectedProviders.some((item) => item.provider === 'fanvue') ? 'connected' : 'not connected'}</StatusPill></div>
                      <p>Fanvue uses OAuth instead of a pasted API key.</p>
                      <div className="v2-action-row"><button type="button" className="v2-btn v2-btn-primary" disabled={!!busy || isTerminalSession} onClick={connectFanvue}>{busy === 'connect-fanvue' ? 'Opening...' : 'Connect Fanvue'}</button>{fanvueConnection.authUrl ? <a className="v2-btn v2-btn-secondary" href={fanvueConnection.authUrl} target="_blank" rel="noreferrer">Open OAuth link</a> : null}</div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="v2-onboarding-connection-grid">
              <div className="v2-onboarding-card"><span>Provider</span><strong>{!readinessHasRun ? 'Not checked' : connectionChecks.provider_connected ? 'Connected' : 'Missing'}</strong><small>{connectedProviders.length ? connectedProviders.map((item) => `${item.provider} · ${item.webhook_status || 'webhook unknown'}`).join(' · ') : 'Connect at least one supported inbox provider.'}</small></div>
              <div className="v2-onboarding-card"><span>Timezone</span><strong>{!readinessHasRun ? 'Not checked' : connectionChecks.timezone_configured ? 'Configured' : 'Missing'}</strong><small>Used for proactive timing, cooldowns and date references.</small></div>
              <div className="v2-onboarding-card"><span>Base config</span><strong>{connectionChecks.base_config?.current_effective ? `#${connectionChecks.base_config.current_effective}` : 'Not checked'}</strong><small>The release is derived from this exact version and fails closed if it changes.</small></div>
            </div>
            <div className="v2-action-row">
              <Link className="v2-btn v2-btn-secondary" to={`/v2/models/${modelId}?tab=Connections`}>Advanced connections</Link>
              <Link className="v2-btn v2-btn-secondary" to={`/v2/models/${modelId}?tab=Setup`}>Open model setup</Link>
              <button type="button" className="v2-btn v2-btn-primary" disabled={!!busy || isTerminalSession} onClick={() => runAction('readiness', () => evaluateOnboardingReadiness(session.id), 'Connection and release readiness recalculated.')}>{busy === 'readiness' ? 'Checking...' : 'Check connection'}</button>
            </div>
          </Section> : null}

          {activeWizardStep === 'import' ? <Section
            eyebrow="2 · Inputs"
            title="Source workspace"
            description="Paste a full brief or import a bounded sample of real chats. Raw source text is redacted after publish by default."
          >
            <div className="v2-onboarding-grid">
              <form className="v2-onboarding-card" onSubmit={handleAddSource}>
                <h3>Describe the creator in your own words</h3>
                <p>Include identity and legend, tone of voice, languages, boundaries, available content and anything she must never claim. It can be unstructured: AI will split it into the correct sections.</p>
                <label className="v2-field"><span>Creator brief</span><textarea rows="10" value={sourceForm.content} onChange={(event) => setSourceForm({ ...sourceForm, content: event.target.value })} placeholder="Example: Alina is 19, studies nursing, writes in casual American English, sells solo content, never offers meetings or calls..." /></label>
                <details className="v2-advanced-disclosure">
                  <summary>Advanced source settings</summary>
                  <div className="v2-form-grid">
                    <label className="v2-field"><span>Name</span><input value={sourceForm.name} onChange={(event) => setSourceForm({ ...sourceForm, name: event.target.value })} /></label>
                    <label className="v2-field"><span>Type</span><select value={sourceForm.source_type} onChange={(event) => setSourceForm({ ...sourceForm, source_type: event.target.value })}><option value="brief">Brief</option><option value="document">Document</option><option value="existing_config">Existing config</option><option value="platform_snapshot">Platform snapshot</option><option value="historical_chat">Pasted chat</option></select></label>
                    <label className="v2-field"><span>Evidence level</span><select value={sourceForm.authority} onChange={(event) => setSourceForm({ ...sourceForm, authority: event.target.value })}><option value="human_confirmed">Confirmed by owner</option><option value="platform_fact">Platform fact</option><option value="approved_document">Approved document</option><option value="historical_observation">Observed in old chats</option><option value="llm_hypothesis">Hypothesis only</option></select></label>
                    <label className="v2-field"><span>Raw text retention</span><select value={sourceForm.retention_mode} onChange={(event) => setSourceForm({ ...sourceForm, retention_mode: event.target.value })}><option value="until_publish">Delete after publish</option><option value="days_30">Keep 30 days</option><option value="keep">Keep until manually removed</option></select></label>
                  </div>
                </details>
                <button type="submit" className="v2-btn v2-btn-primary" disabled={!!busy || !sourceForm.content.trim() || isTerminalSession}>{busy === 'add-source' ? 'Saving...' : 'Save brief for AI review'}</button>
                <div className="v2-onboarding-upload">
                  <label className="v2-field"><span>Or upload an existing brief / playbook</span><input ref={sourceFileInput} type="file" accept=".txt,.md,.json,.csv,.tsv,.html,.htm,.docx,.pdf" disabled={!!busy || isTerminalSession} onChange={(event) => setSourceFile(event.target.files?.[0] || null)} /></label>
                  <span>Upload a legend, tone-of-voice guide, sales playbook or boundaries document. TXT, Markdown, JSON, CSV, HTML, DOCX and text PDFs are supported up to 10 MB. The original binary is not retained.</span>
                  <button type="button" className="v2-btn v2-btn-secondary" disabled={!!busy || !sourceFile || isTerminalSession} onClick={handleUploadSource}>{busy === 'upload-source' ? 'Extracting...' : 'Use this document'}</button>
                </div>
              </form>

              <div className="v2-onboarding-card">
                <h3>Import historical chats</h3>
                <p>Recommended when the agency already has chats. AI uses a bounded sample to learn useful voice patterns and show where chatters lost sales or future LTV. Importing never changes or sends production messages.</p>
                <div className="v2-form-grid">
                  <label className="v2-field"><span>Lookback days</span><input type="number" min="1" max="365" value={historyForm.days} onChange={(event) => setHistoryForm({ ...historyForm, days: Number(event.target.value) })} /></label>
                  <label className="v2-field"><span>Max conversations</span><input type="number" min="1" max="100" value={historyForm.max_conversations} onChange={(event) => setHistoryForm({ ...historyForm, max_conversations: Number(event.target.value) })} /></label>
                  <label className="v2-field"><span>Minimum messages</span><input type="number" min="2" max="100" value={historyForm.min_messages} onChange={(event) => setHistoryForm({ ...historyForm, min_messages: Number(event.target.value) })} /></label>
                </div>
                <button type="button" className="v2-btn v2-btn-secondary" disabled={!!busy || isTerminalSession} onClick={handleImportHistory}>{busy === 'import-history' ? 'Importing...' : 'Import sample'}</button>
                <div className="v2-onboarding-privacy-note"><strong>Privacy boundary</strong><span>Only the selected bounded copy enters onboarding analysis. Production message rows remain untouched.</span></div>
              </div>
            </div>

            <div className="v2-onboarding-source-list">
              {session.sources.length ? session.sources.map((source) => (
                <article key={source.id}>
                  <div><strong>{source.name}</strong><span>{source.source_type.replaceAll('_', ' ')} · {source.authority.replaceAll('_', ' ')}</span></div>
                  <StatusPill tone={statusTone(source.status)}>{source.status}</StatusPill>
                  <span>{source.metadata?.message_count ? `${source.metadata.message_count} logical messages` : source.content_hash.slice(0, 10)}</span>
                  <button type="button" className="v2-btn v2-btn-ghost" disabled={!!busy || source.status === 'excluded' || isTerminalSession} onClick={() => handleRemoveSource(source)}>{source.status === 'excluded' ? 'Excluded' : source.status === 'pending' ? 'Remove' : 'Exclude'}</button>
                </article>
              )) : <EmptyState title="No sources" description="Add a confirmed brief or import historical chats." />}
            </div>
            <div className="v2-action-row v2-onboarding-primary-action">
              <button type="button" className="v2-btn v2-btn-primary" disabled={!!busy || !activeSources.length || session.status === 'analyzing' || isTerminalSession} onClick={() => runAction('analyze', () => analyzeOnboarding(session.id, { force: false }), 'Pointed analyzers queued.')}>{session.status === 'analyzing' ? 'Analyzing...' : 'Analyze sources'}</button>
              <span>Bounded source text is sent to the configured analyzer. MiniMax gets two validated attempts; OpenAI is used only after both fail.</span>
            </div>
          </Section> : null}

          {activeWizardStep === 'review' ? <Section
            eyebrow="3 · Human review"
            title="Configuration proposals"
            description="Nothing enters the draft until you accept or edit it. Evidence remains attached to every proposal. AI confidence is an uncalibrated extraction score, not a probability."
          >
            {candidates.length ? (
              <div className="v2-onboarding-review-list">
                {candidates.map((candidate) => (
                  <article key={candidate.id} className={`v2-onboarding-review-card is-${candidate.status}`}>
                    <div className="v2-onboarding-review-head">
                      <div><span>{candidate.section}</span><strong>{candidate.candidate_key.replaceAll('_', ' ')}</strong></div>
                      <div className="v2-action-row"><StatusPill tone={statusTone(candidate.status)}>{candidate.status}</StatusPill><StatusPill>AI confidence {candidate.confidence}/100</StatusPill><StatusPill tone={candidate.supporting_source_count > 1 ? 'good' : 'neutral'}>{candidate.supporting_source_count} active source{candidate.supporting_source_count === 1 ? '' : 's'}</StatusPill></div>
                    </div>
                    {editingCandidate === candidate.id ? (
                      <textarea rows="6" value={candidateText} onChange={(event) => setCandidateText(event.target.value)} />
                    ) : <div className="v2-onboarding-value-list">{displayRows(candidate.payload?.value ?? candidate.payload).map((row, index) => <div key={`${row.label}-${index}`}><span>{row.label}</span><strong>{row.value}</strong></div>)}</div>}
                    <p className="v2-onboarding-evidence">{evidenceText(candidate.evidence)}</p>
                    {!candidate.is_current ? <p className="v2-onboarding-warnings">This proposal is historical only: its analyzer run was superseded or its source was excluded.</p> : null}
                    <div className="v2-action-row">
                      <button type="button" className="v2-btn v2-btn-primary" disabled={!!busy || isTerminalSession || !candidate.is_current || ['accepted', 'edited'].includes(candidate.status)} onClick={() => handleCandidate(candidate, 'accepted')}>{['accepted', 'edited'].includes(candidate.status) ? 'Included' : 'Accept'}</button>
                      {editingCandidate === candidate.id ? (
                        <><button type="button" className="v2-btn v2-btn-secondary" disabled={!!busy || isTerminalSession || !candidate.is_current} onClick={() => saveCandidateEdit(candidate)}>Save edit</button><button type="button" className="v2-btn v2-btn-ghost" onClick={() => setEditingCandidate(null)}>Cancel</button></>
                      ) : <button type="button" className="v2-btn v2-btn-secondary" disabled={!!busy || isTerminalSession || !candidate.is_current} onClick={() => { setEditingCandidate(candidate.id); setCandidateText(displayValue(candidate.payload?.value ?? candidate.payload)) }}>Edit</button>}
                      <button type="button" className="v2-btn v2-btn-ghost" disabled={!!busy || isTerminalSession} onClick={() => handleCandidate(candidate, 'rejected')}>Reject</button>
                    </div>
                  </article>
                ))}
              </div>
            ) : <EmptyState title="No proposals yet" description="Add a creator brief or import historical chats, then ask AI to structure them. Nothing is accepted automatically." action={<button type="button" className="v2-btn v2-btn-secondary" onClick={() => setActiveWizardStep('import')}>Go to Add information</button>} />}
          </Section> : null}

          {activeWizardStep === 'audit' ? <Section
            eyebrow="4 · Historical chat audit"
            title="What to preserve, fix, stop and test"
            description="The report can flag session-level revenue wins and possible future-LTV damage separately. Counterfactual uplift is never presented as fact."
          >
            <div className="v2-tabs v2-onboarding-audit-tabs">
              {AUDIT_BUCKETS.map((bucket) => <button key={bucket} type="button" className={auditBucket === bucket ? 'is-active' : ''} onClick={() => setAuditBucket(bucket)}>{bucket} ({findings.filter((item) => item.bucket === bucket).length})</button>)}
            </div>
            {bucketFindings.length ? (
              <div className="v2-onboarding-findings">
                {bucketFindings.map((finding) => (
                  <article key={finding.id}>
                    <div className="v2-onboarding-review-head"><div><span>{finding.category.replaceAll('_', ' ')}</span><strong>{finding.title}</strong></div><div className="v2-action-row"><StatusPill tone={finding.severity === 'critical' || finding.severity === 'high' ? 'bad' : finding.severity === 'medium' ? 'warn' : 'neutral'}>{finding.severity}</StatusPill><StatusPill tone={statusTone(finding.status)}>{finding.status}</StatusPill></div></div>
                    <p>{finding.summary}</p>
                    {finding.recommendation ? <p><strong>Recommendation:</strong> {finding.recommendation}</p> : null}
                    <p className="v2-onboarding-evidence">{evidenceText(finding.evidence)}</p>
                    {finding.status === 'open' ? <><label className="v2-field v2-onboarding-risk-note"><span>Review note (required to accept risk)</span><textarea rows="2" maxLength="4000" value={findingNotes[finding.id] || ''} onChange={(event) => setFindingNotes((current) => ({ ...current, [finding.id]: event.target.value }))} placeholder="Why is this risk acceptable for this release?" /></label><div className="v2-action-row"><button type="button" className="v2-btn v2-btn-secondary" disabled={!!busy || isTerminalSession} onClick={() => handleFindingReview(finding, 'resolved')}>Resolve</button><button type="button" className="v2-btn v2-btn-ghost" disabled={!!busy || isTerminalSession || !String(findingNotes[finding.id] || '').trim()} onClick={() => handleFindingReview(finding, 'accepted_risk')}>Accept risk</button></div></> : null}
                  </article>
                ))}
              </div>
            ) : <EmptyState title={`No ${auditBucket} findings`} description="This bucket is empty for the analyzed evidence." />}
          </Section> : null}

          {activeWizardStep === 'configure' ? <Section
            eyebrow="5 · Voice & behavior"
            title="Decide how the model should chat and sell"
            description="Answer business questions in plain language. FanLTV compiles these answers into deterministic graph settings and generator guidance."
          >
            <div className="v2-onboarding-ai-assist">
              <div><strong>Generate the profile with AI</strong><span>AI can turn the uploaded brief and historical chats into a proposed legend, voice, boundaries and sales rules. You will review every proposal before it is used.</span></div>
              <button type="button" className="v2-btn v2-btn-secondary" disabled={!!busy || !activeSources.length || session.status === 'analyzing' || isTerminalSession} onClick={() => runAction('analyze', () => analyzeOnboarding(session.id, { force: true }), 'AI profile generation queued. Review the proposals when analysis completes.')}>{session.status === 'analyzing' ? 'Generating...' : 'Generate or refresh with AI'}</button>
            </div>

            <div className="v2-onboarding-question-grid">
              <label className="v2-onboarding-question"><span>Who usually leads the conversation?</span><small>Choose whether this model should mostly react or actively create momentum.</small><select value={behaviorPlan.conversation_lead} onChange={(event) => updateBehavior('conversation_lead', event.target.value)}><option value="responsive">Mostly respond to the fan</option><option value="balanced">Balanced — respond and lead when useful</option><option value="creator_led">Creator-led — initiate flirt and direction</option></select></label>
              <label className="v2-onboarding-question"><span>How quickly should the bot move toward a sale?</span><small>Safety, refusal, affordability and cooldown gates remain active in every mode.</small><select value={behaviorPlan.sales_pacing} onChange={(event) => updateBehavior('sales_pacing', event.target.value)}><option value="gentle">Gentle — wait for clearer interest</option><option value="balanced">Balanced — build interest, then progress</option><option value="proactive">Proactive — create and act on opportunities</option></select></label>
              <label className="v2-onboarding-question"><span>Should PRESELL ask whether the fan is alone or private?</span><small>This controls the repeated qualification pattern without disabling safety checks.</small><select value={behaviorPlan.presell_privacy_check} onChange={(event) => updateBehavior('presell_privacy_check', event.target.value)}><option value="ask_once">Ask once when not confirmed</option><option value="contextual">Only when genuinely needed</option><option value="skip_routine">Do not use it as a routine sales gate</option></select></label>
              <label className="v2-onboarding-question"><span>Typical reply style</span><small>The bot can still write longer when the context genuinely needs it.</small><select value={behaviorPlan.reply_style} onChange={(event) => updateBehavior('reply_style', event.target.value)}><option value="short">Short and direct</option><option value="natural">Natural chat length</option><option value="expressive">More expressive</option></select></label>
              <label className="v2-onboarding-question"><span>Punctuation style</span><small>Minimal punctuation usually feels more casual; URLs and prices are preserved.</small><select value={behaviorPlan.punctuation_style} onChange={(event) => updateBehavior('punctuation_style', event.target.value)}><option value="minimal">Casual / minimal</option><option value="standard">Standard punctuation</option></select></label>
              <label className="v2-onboarding-question"><span>How should longer replies arrive?</span><small>Natural bursts use short message bubbles with typing pauses; the Core still keeps one action per turn.</small><select value={behaviorPlan.delivery_style} onChange={(event) => updateBehavior('delivery_style', event.target.value)}><option value="natural_bursts">Several natural chat bubbles</option><option value="single_message">One complete message</option></select></label>
              <label className="v2-onboarding-question"><span>Emoji policy</span><small>Mirroring avoids suddenly adding emojis when the fan does not use them.</small><select value={behaviorPlan.emoji_policy} onChange={(event) => updateBehavior('emoji_policy', event.target.value)}><option value="mirror_fan">Use only after the fan uses them</option><option value="occasional">Allow occasional natural emojis</option></select></label>
              <label className="v2-onboarding-question"><span>When the fan explicitly asks to buy</span><small>A direct request should not be forced through every rapport or PRESELL stage.</small><select value={behaviorPlan.explicit_buy_flow} onChange={(event) => updateBehavior('explicit_buy_flow', event.target.value)}><option value="direct">Serve the request directly</option><option value="preview_first">Give a preview before the paid step</option></select></label>
              <label className="v2-onboarding-question"><span>When the fan says the price is too high</span><small>A clear refusal or affordability problem still stops pressure in every mode.</small><select value={behaviorPlan.price_objection_strategy} onChange={(event) => updateBehavior('price_objection_strategy', event.target.value)}><option value="protect_price">Protect the price; do not discount</option><option value="ask_budget">Ask the budget before changing the offer</option><option value="small_discount">Allow a small configured discount</option></select></label>
              <label className="v2-onboarding-question"><span>Proactive follow-up</span><small>Adaptive follow-up increases the interval after unanswered messages.</small><select value={behaviorPlan.proactive_followup} onChange={(event) => updateBehavior('proactive_followup', event.target.value)}><option value="off">Off</option><option value="adaptive">Adaptive timing</option><option value="active">Active follow-up</option></select></label>
            </div>
            <div className="v2-onboarding-toggle-grid">
              <DraftToggle label="After a clearly positive reaction, allow the Core to progress instead of asking for another confirmation" checked={behaviorPlan.offer_after_positive_reaction} onChange={(value) => updateBehavior('offer_after_positive_reaction', value)} />
              <DraftToggle label="Allow one free preview step before the paid continuation when the route supports it" checked={behaviorPlan.free_preview_then_paid} onChange={(value) => updateBehavior('free_preview_then_paid', value)} />
            </div>
            <div className="v2-onboarding-confirm-bar">
              <DraftToggle label="I confirm these conversation and sales choices" checked={behaviorPlan.confirmed} onChange={(value) => { setBehaviorDirty(true); setBehaviorPlan((current) => ({ ...current, confirmed: value })) }} />
              <button type="button" className="v2-btn v2-btn-primary" disabled={!!busy || isTerminalSession || !behaviorPlan.confirmed} onClick={saveBehaviorPlan}>{busy === 'save-behavior' ? 'Saving...' : behaviorDirty ? 'Save behavior choices' : 'Save again'}</button>
            </div>

            <details className="v2-onboarding-advanced-profile">
              <summary>Advanced: review the generated profile sections</summary>
              <p>Most agencies do not need to edit these fields directly. Use them only to correct a specific fact AI could not structure correctly.</p>
              <div className="v2-onboarding-draft-layout">
                <nav aria-label="Draft sections">
                  {DRAFT_SECTIONS.map((sectionName) => <button key={sectionName} type="button" className={draftSection === sectionName ? 'is-active' : ''} onClick={() => setDraftSection(sectionName)}><span>{sectionName.replaceAll('_', ' ')}</span><StatusPill tone={session.draft_sections?.[sectionName] ? 'good' : 'neutral'}>{session.draft_sections?.[sectionName] ? 'set' : 'empty'}</StatusPill></button>)}
                </nav>
                <div className="v2-onboarding-draft-editor">
                  <div><h3>{draftSection.replaceAll('_', ' ')}</h3><p>Plain text or valid JSON are both accepted.</p></div>
                  <textarea rows="18" value={draftText} onChange={(event) => setDraftText(event.target.value)} />
                  <button type="button" className="v2-btn v2-btn-primary" disabled={!!busy || isTerminalSession} onClick={saveDraft}>{busy === 'save-draft' ? 'Saving...' : 'Save section'}</button>
                </div>
              </div>
            </details>
          </Section> : null}

          {activeWizardStep === 'monetization' ? <Section
            eyebrow="6 · Products and content"
            title="Choose how this model can monetize"
            description="Routes are independent. A model can launch in chat-only mode, sell catalog media without scripts, or enable only the capabilities that are actually configured. These values stay isolated until Publish."
          >
            <div className="v2-onboarding-content-checkpoint">
              <div><span>Content workspace</span><strong>{!readinessHasRun ? 'Run readiness to inspect the inventory' : `${contentInventory.eligible || 0} eligible · ${contentInventory.catalog_sellable || 0} sellable · ${contentInventory.valid_scripts || 0} valid scripts`}</strong><p>Describe and group the media the bot may use, then set real prices. Sale scripts are required only for scripted or hybrid catalog strategy.</p></div>
              <Link className="v2-btn v2-btn-secondary" to={`/v2/models/${modelId}?tab=Content`}>Open content workspace</Link>
            </div>
            <div className="v2-onboarding-card">
              <div><span>First</span><h3>What can this creator actually sell?</h3><p>Select only real capabilities. Detailed settings appear only for the products you enable.</p></div>
              <div className="v2-onboarding-capability-grid">
                {Object.entries(ROUTE_META).map(([routeKey, meta]) => {
                  const route = monetizationPlan.routes?.[routeKey] || EMPTY_MONETIZATION_PLAN.routes[routeKey]
                  const enabled = route.mode !== 'disabled'
                  const defaultMode = ['customs', 'physical_items'].includes(routeKey) ? 'human_handoff' : 'autonomous'
                  return <label key={routeKey} className={`v2-onboarding-platform-choice ${enabled ? 'is-selected' : ''}`.trim()}><input type="checkbox" checked={enabled} disabled={!!busy || isTerminalSession} onChange={(event) => updateMonetizationRoute(routeKey, { mode: event.target.checked ? defaultMode : 'disabled' })} /><span><strong>{meta.title}</strong><small>{meta.description}</small></span></label>
                })}
              </div>
            </div>
            <div className="v2-onboarding-route-list">
              {Object.entries(ROUTE_META).filter(([routeKey]) => (monetizationPlan.routes?.[routeKey] || EMPTY_MONETIZATION_PLAN.routes[routeKey]).mode !== 'disabled').map(([routeKey, meta]) => {
                const route = monetizationPlan.routes?.[routeKey] || EMPTY_MONETIZATION_PLAN.routes[routeKey]
                const status = monetizationDirty || !readinessIsCurrent ? null : routeReadiness[routeKey]
                return (
                  <article key={routeKey} className="v2-onboarding-route-card">
                    <div className="v2-onboarding-review-head">
                      <div><span>Settings for enabled capability</span><strong>{meta.title}</strong></div>
                      <div className="v2-action-row">
                        {status ? <StatusPill tone={status.ready ? 'good' : 'warn'}>{status.ready ? 'ready' : 'needs setup'}</StatusPill> : <StatusPill>{monetizationDirty ? 'unsaved' : 'not checked'}</StatusPill>}
                        <select value={route.mode || 'disabled'} disabled={!!busy || isTerminalSession} onChange={(event) => updateMonetizationRoute(routeKey, { mode: event.target.value })}>
                          {(ROUTE_MODES[routeKey] || []).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                        </select>
                      </div>
                    </div>
                    <p>{meta.description}</p>
                    {(status?.blockers || []).length ? <ul className="v2-onboarding-route-issues">{status.blockers.map((item) => <li key={item}>{item.replaceAll('_', ' ')}</li>)}</ul> : null}
                    {(status?.warnings || []).length ? <ul className="v2-onboarding-warnings">{status.warnings.map((item) => <li key={item}>{item.replaceAll('_', ' ')}</li>)}</ul> : null}
                    <RouteConfigFields
                      routeKey={routeKey}
                      route={route}
                      modelId={modelId}
                      contentInventory={contentInventory}
                      onRouteChange={(patch) => updateMonetizationRoute(routeKey, patch)}
                      onConfigChange={(key, value) => updateMonetizationConfig(routeKey, key, value)}
                    />
                  </article>
                )
              })}
              {Object.values(monetizationPlan.routes || {}).every((route) => route.mode === 'disabled') ? <EmptyState title="Chat-only mode" description="No monetization capability is selected. You can continue in chat-only mode or enable a product above." /> : null}
            </div>
            <div className="v2-onboarding-confirm-bar">
              <DraftToggle label="I confirm these are the monetization routes and real capabilities this model may use" checked={monetizationPlan.confirmed} onChange={(value) => { setMonetizationDirty(true); setMonetizationPlan((current) => ({ ...current, confirmed: value })) }} />
              <button type="button" className="v2-btn v2-btn-primary" disabled={!!busy || isTerminalSession || !monetizationPlan.confirmed} onClick={saveMonetizationPlan}>{busy === 'save-monetization' ? 'Saving...' : 'Save monetization plan'}</button>
            </div>
            <div className="v2-onboarding-privacy-note"><strong>Scripts are optional</strong><span>Choose intent based for a model with a described, priced catalog but no fixed sale script. Scripted and hybrid modes require at least one complete script.</span></div>
          </Section> : null}

          {activeWizardStep === 'test' ? <Section
            eyebrow="7 · Test and publish"
            title="Readiness, snapshot and publish"
            description="Tests bind to the exact snapshot hash. Any later draft change requires a new release. Publishing does not unpause automation."
          >
            <div className="v2-onboarding-release-grid">
              <div className="v2-onboarding-card">
                <h3>Readiness</h3>
                <StatusPill tone={releaseReady ? 'good' : 'warn'}>{!readinessHasRun ? 'not checked' : !readinessIsCurrent ? 'stale' : releaseReady ? 'ready' : 'not ready'}</StatusPill>
                {(readiness.blockers || []).length ? <ul>{readiness.blockers.map((item) => <li key={item}>{item.replaceAll('_', ' ')}</li>)}</ul> : <p>{!readinessHasRun ? 'Run readiness to check the required setup.' : 'No blockers recorded.'}</p>}
                {(readiness.warnings || []).length ? <ul className="v2-onboarding-warnings">{readiness.warnings.map((item) => <li key={item}>{item.replaceAll('_', ' ')}</li>)}</ul> : null}
                <button type="button" className="v2-btn v2-btn-secondary" disabled={!!busy || isTerminalSession} onClick={() => runAction('readiness', () => evaluateOnboardingReadiness(session.id), 'Readiness recalculated.')}>Run readiness</button>
              </div>
              <div className="v2-onboarding-card">
                <h3>Create immutable snapshot</h3>
                <label className="v2-field"><span>Release label</span><input value={releaseLabel} onChange={(event) => setReleaseLabel(event.target.value)} placeholder="e.g. Launch calibration" /></label>
                <button type="button" className="v2-btn v2-btn-primary" disabled={!!busy || pendingCandidates > 0 || openStops > 0 || !releaseReady || isTerminalSession} onClick={() => runAction('create-release', () => createOnboardingRelease(session.id, releaseLabel), 'Release snapshot and isolated draft config created.')}>Create release</button>
                {pendingCandidates ? <p>{pendingCandidates} proposals still need review.</p> : null}
                {openStops ? <p>{openStops} high-risk stop findings remain open.</p> : null}
              </div>
            </div>

            {latestRelease ? (
              <article className="v2-onboarding-release-card">
                <div className="v2-onboarding-review-head"><div><span>Release v{latestRelease.version}</span><strong>{latestRelease.snapshot_hash.slice(0, 16)}…</strong></div><StatusPill tone={statusTone(latestRelease.status)}>{latestRelease.status}</StatusPill></div>
                <div className="v2-onboarding-release-stats"><span>Config #{latestRelease.graph_config_id || '—'}</span><span>{latestRelease.test_summary?.passed || 0} checks passed</span><span>{latestRelease.test_summary?.failed || 0} failed</span></div>
                {(latestRelease.test_summary?.checks || []).length ? <div className="v2-onboarding-checks">{latestRelease.test_summary.checks.map((item) => <div key={item.key} className={item.passed ? 'is-passed' : 'is-failed'}><strong>{item.passed ? '✓' : '×'} {item.key.replaceAll('_', ' ')}</strong><span>{item.detail}</span></div>)}</div> : null}
                {(latestRelease.test_summary?.warnings || []).length ? <div className="v2-onboarding-checks">{latestRelease.test_summary.warnings.map((item) => <div key={item.key} className="is-warning"><strong>! {item.key.replaceAll('_', ' ')}</strong><span>{item.detail}</span></div>)}</div> : null}
                {latestRelease.test_summary?.behavior_execution ? <div className="v2-onboarding-behavior-tests"><div className="v2-action-row"><strong>Isolated graph tests</strong><StatusPill tone={statusTone(latestRelease.test_summary.behavior_execution.status)}>{latestRelease.test_summary.behavior_execution.status}</StatusPill></div>{(latestRelease.test_summary.behavior_execution.results || []).map((item) => <div key={item.key} className={item.passed ? 'is-passed' : 'is-failed'}><span>{item.passed ? '✓' : '×'} {item.name || item.key}</span><small>{item.error || item.safety_lock_reason || `test run #${item.test_run_id || '—'}`}</small></div>)}</div> : null}
                <div className="v2-action-row">
                  <button type="button" className="v2-btn v2-btn-secondary" disabled={!!busy || isTerminalSession || latestRelease.status === 'published' || ['queued', 'running'].includes(latestRelease.test_summary?.behavior_execution?.status)} onClick={() => runAction('test-release', () => testOnboardingRelease(session.id, latestRelease.id), 'Snapshot-bound preflight completed; isolated graph tests queued.')}>Run release tests</button>
                  {latestRelease.status === 'tested' && requiresWarningAck ? <label className="v2-check-row"><input type="checkbox" checked={ackWarnings} onChange={(event) => setAckWarnings(event.target.checked)} /><span>I reviewed and accept non-blocking warnings.</span></label> : null}
                  <button type="button" className="v2-btn v2-btn-primary" disabled={!!busy || !canPublishOnboardingRelease(session, latestRelease) || (requiresWarningAck && !ackWarnings)} onClick={handlePublishRelease}>Publish</button>
                  {latestRelease.status === 'published' ? <button type="button" className="v2-btn v2-btn-ghost" disabled={!!busy} onClick={handleRollbackRelease}>Rollback</button> : null}
                </div>
              </article>
            ) : null}
          </Section> : null}
          <div className="v2-onboarding-wizard-actions">
            <button type="button" className="v2-btn v2-btn-secondary" disabled={activeWizardStep === STEPS[0][0]} onClick={() => moveWizard(-1)}>Back</button>
            <span>{STEP_HELP[activeWizardStep]?.required ? 'You can continue now, but this step must be complete before Publish.' : 'This step can be skipped and revisited later.'}</span>
            <button type="button" className="v2-btn v2-btn-primary" disabled={activeWizardStep === STEPS[STEPS.length - 1][0]} onClick={() => moveWizard(1)}>{STEP_HELP[activeWizardStep]?.required ? 'Continue' : 'Continue or skip'}</button>
          </div>
          </main>
          </div>
        </>
      )}
    </div>
  )
}
